import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient.web";
import Turnstile, { type TurnstileHandle } from "../lib/Turnstile";
import { TOS_PATH, tosAcceptance } from "../lib/tos";
import { MIN_PASSWORD_LENGTH, passwordMeetsPolicy } from "../lib/passwordPolicy";
import PasswordRules from "../lib/PasswordRules";
import { inputsFromEmail, loadPasswordEstimator } from "../lib/passwordStrength";
import PasswordStrengthMeter from "../lib/PasswordStrengthMeter";
import { authCodeFor, authMessageFor } from "../lib/errorCodes";
import BrandHeader from "../lib/BrandHeader";

/**
 * What the page says when a signup is refused.
 *
 * ---------------------------------------------------------------------------
 * THIS IS THE ONLY PAGE REACHABLE WITHOUT AN ACCOUNT, AND IT MUST NOT SAY WHICH
 * EMAIL ADDRESSES HAVE ONE (10.2.2's rule, on the one page it matters most).
 *
 * ONE VAGUE SENTENCE WAS NOT ENOUGH, and until Phase 12.5 that was all there
 * was. It hid WHY a signup failed, not THAT it failed - and the page itself
 * rules out every other reason: the checklist counts what the server counts,
 * the human check shows "Success!", and the button is dead without the Terms
 * box. Everything visibly right and still refused leaves one likely cause, an
 * address that already has an account. And a new address moved on to "check
 * your email" while a taken one stayed here, so the outcome said it anyway.
 *
 * THE SERVER NOW ANSWERS BOTH ALIKE. With email AND phone confirmations on
 * (`[auth.sms] enable_confirmations` in config.toml - phone signup stays off),
 * GoTrue v2.191.0 answers a signup for an address that already has an account
 * with a made-up user and no session, sends nothing, and returns 200 - as for a
 * new address. Measured, with one captcha token each:
 *
 *   before   registered 422 user_already_exists    new 200
 *   after    registered 200, identities []         new 200, identities [1]
 *
 * So both move on to "check your email". What the page cannot hide is the
 * response itself: the made-up user's empty `identities` is readable in the
 * browser's developer tools. That needs signup behind our own endpoint to
 * close, and is on Phase 14's list; each probe still costs a solved challenge,
 * because GoTrue checks the captcha first.
 *
 * ONE REFUSAL ALSO MOVES ON: `over_email_send_rate_limit`. Two signups for one
 * address inside `max_frequency` are refused only when the first one sent an
 * email - a new or unconfirmed address, never one with a confirmed account,
 * which is sent nothing. Shown as a failure, that is the same tell again. It is
 * honest to move on: an email went to that address moments ago. The cost is
 * the project-wide email limit (`[auth.rate_limit] email_sent`), which returns
 * the same code having sent nothing - rare, and every signup is failing then.
 * `over_request_rate_limit`, the per-connection limit, is left a refusal: it
 * does not depend on the address, and nothing was sent.
 *
 * Every other refusal - no connection, a server error, a malformed address -
 * shows this one sentence, and none of them depends on who has an account.
 * ---------------------------------------------------------------------------
 */
const REFUSED =
  "That did not work. Check the address and password and try again.";

export default function SignupPage() {
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const turnstile = useRef<TurnstileHandle | null>(null);

  const emailRedirectTo = useMemo(() => {
    // Where the verification link should send the user back after confirming.
    // Keeping this simple: route them to /connect which is already protected/gated.
    return `${window.location.origin}/connect`;
  }, []);

  // The button is disabled without the checkbox, and the BACKEND refuses
  // without it too - the before_user_created hook, not this line, is what makes
  // "a signup cannot complete without TOS acceptance" true. A disabled button
  // is a courtesy; signup is a direct call to GoTrue that a form cannot gate.
  const canSubmit = acceptedTos && !!captchaToken && !busy;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!canSubmit) return;

    // Checked here, before the request, because Phase 10.5 raised the server's
    // `minimum_password_length` from 6 to 12 and this form had no length check
    // at all. Without this, a short password comes back as GoTrue's
    // `weak_password` and is shown through REFUSED - one deliberately vague
    // sentence that would leave the user with no idea what was wrong.
    //
    // SAFE TO SHOW SPECIFICALLY, unlike every other refusal on this page: it is
    // about the password the user just typed, and says nothing about whether
    // the ADDRESS is registered. The oracle rule REFUSED exists for is about
    // the address. It also never reaches the network, so it costs no captcha.
    if (!passwordMeetsPolicy(password)) {
      setError(authMessageFor("WEAK_PASSWORD"));
      return;
    }

    // Too easy to guess (Phase 12.5): refused below a score of 3, not only below
    // 12 characters. A courtesy, unlike the rule check above - the server knows
    // the length and the character types, not the patterns - and safe to say
    // for the same reason. If the estimator
    // could not be loaded, the length rule stands alone rather than refusing
    // everyone (passwordStrength.ts).
    const estimate = await loadPasswordEstimator().catch(() => null);
    if (estimate && !estimate(password, inputsFromEmail(email)).acceptable) {
      setError(authMessageFor("PASSWORD_TOO_GUESSABLE"));
      return;
    }

    setBusy(true);

    const e1 = email.trim();

    const { data, error } = await supabase.auth.signUp({
      email: e1,
      password,
      options: {
        emailRedirectTo,
        captchaToken: captchaToken ?? undefined,
        data: tosAcceptance(),
      },
    });

    setBusy(false);

    // Refused only because an email went to this address moments ago - see
    // REFUSED. Checked on GoTrue's own code, not authCodeFor's RATE_LIMITED,
    // which also covers the per-connection limit that must stay a refusal.
    const emailedMomentsAgo = error?.code === "over_email_send_rate_limit";

    if (error && !emailedMomentsAgo) {
      // The token has been spent whether or not the signup succeeded, so the
      // widget is reset before the user can press the button again.
      turnstile.current?.reset();
      setCaptchaToken(null);
      // A leaked password (the hosted project's HaveIBeenPwned check) is named,
      // for the reason the rule check above is: it is about the password. GoTrue
      // checks the password in validateSignupParams, BEFORE it looks the address
      // up (v2.191.0 signup.go), so a registered and a new address get the same
      // refusal and this says nothing about either.
      if (authCodeFor(error) === "PASSWORD_BREACHED") {
        setError(authMessageFor("PASSWORD_BREACHED"));
        return;
      }
      setError(REFUSED);
      return;
    }

    // With email confirmation on, session is null - for a new address, for one
    // that already has an account (GoTrue's made-up user), and for one emailed
    // moments ago.
    if (!data.session) {
      nav(`/auth/verify?email=${encodeURIComponent(e1)}&returnTo=${encodeURIComponent("/connect")}`, {
        replace: true,
      });
      return;
    }

    // If confirmations are disabled (dev), proceed directly.
    nav("/connect", { replace: true });
  }

  return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface shadow p-6">
        <BrandHeader />
        <h1 className="text-xl font-semibold">Create your account</h1>

        <form className="mt-6 space-y-3" onSubmit={onSubmit}>
          <div>
            <label className="text-sm font-medium" htmlFor="email">Email</label>
            <input
              id="email"
              className="mt-1 w-full rounded-xl border border-line-control px-3 py-2"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium" htmlFor="password">Password</label>
            <input
              id="password"
              className="mt-1 w-full rounded-xl border border-line-control px-3 py-2"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
            />
            <PasswordRules password={password} />
            <PasswordStrengthMeter password={password} userInputs={inputsFromEmail(email)} rulesMet={passwordMeetsPolicy(password)} />
          </div>

          <div className="pt-1">
            <Turnstile ref={turnstile} onToken={setCaptchaToken} />
          </div>

          {/*
            THE CHECKBOX SITS DIRECTLY ABOVE THE BUTTON, AND THAT IS THE POINT
            (11.3). It is the usual place for it, and it is what lets the hint
            under the button go: an unticked box one line above a dead button
            explains the button without a sentence saying so.

            The words "Terms of Service" ARE the link, opening in a new tab -
            the separate "show" link the FR asked for is not a pattern anyone
            uses, and a user who opens the Terms mid-signup must not lose what
            they have typed.

            GONE FROM THE SCREEN, NOT FROM THE RECORD: "(version 2026-09-09)".
            Sign-up forms do not show a version, and no user can act on it.
            `tosAcceptance()` still writes TOS_VERSION into the user's metadata
            and the before_user_created hook still refuses a signup that does
            not carry the current one (9.6) - so what is enforced is unchanged,
            and only the display went.
          */}
          <div className="flex items-start gap-2 pt-1">
            <input
              id="tos"
              type="checkbox"
              className="mt-1"
              checked={acceptedTos}
              onChange={(e) => setAcceptedTos(e.target.checked)}
            />
            <label htmlFor="tos" className="text-sm text-fg-body">
              I agree to the{" "}
              <Link
                className="text-link hover:underline"
                to={TOS_PATH}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </Link>
            </label>
          </div>

          {error && (
            <div className="rounded-xl border border-danger-line bg-danger-bg p-3 text-sm text-danger">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-2 w-full rounded-xl bg-primary text-on-primary py-2 disabled:opacity-60"
          >
            {busy ? "Creating…" : "Sign up"}
          </button>

          {/* No standing hint under the button (11.3). Both things it used to
              name are now visible a line above it - an unticked checkbox and an
              unsolved widget - and the one case it genuinely explained, a check
              that never loaded, is said by the widget itself only when it is
              true (TURNSTILE_FAILED in Turnstile.tsx). `canSubmit` is
              unchanged: no ticked box and no token, no signup. */}

          <div className="text-sm text-fg-soft">
            Already have an account?{" "}
            <Link className="text-link hover:underline" to="/auth/login">
              Log in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
